import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import * as Clipboard from 'expo-clipboard';
import React from 'react';
import { View, Text, ScrollView, ActivityIndicator, StyleSheet, Alert, Platform, Share } from 'react-native';
import { Provider as PaperProvider } from 'react-native-paper';

import {
  isScreenshotMode,
  isScreenshotGuestMode,
  DEMO_USER_ID,
  DEMO_FAMILY,
  DEMO_FAMILY_MEMBERS,
  DEMO_FRIENDS_GROUP,
  DEMO_FRIENDS_MEMBERS,
} from '../../../lib/screenshotMode';
import { t } from '../../i18n/translations';
import { useGuestUser } from '../../shared/contexts/GuestUserContext';
import { useAuth } from '../../shared/hooks/useAuth';
import { supabase } from '../../shared/services/supabaseService';
import Card from '../../shared/ui/Card';
import PrimaryButton from '../../shared/ui/PrimaryButton';
import { PremiumFeatureGate, preWarmFeatureCache } from '../monetization/components/PremiumFeatureGate';
import { PremiumFeature } from '../monetization/types/premiumFeatures';
import { useLanguageStore } from '../quiz-core/languageStore';
import { useUserProfile } from '../quiz-core/useStore';

import { FamilyLeaderboard } from './components/FamilyLeaderboard';
import CreateGroupModal from './CreateGroupModal';
import FamilyCard from './FamilyCard';
import FamilyMembersModal from './FamilyMembersModal';
import FriendsCard from './FriendsCard';
import InvitationCard from './InvitationCard';
import JoinByInviteCodeModal from './JoinByInviteCodeModal';
import { useFamilyData } from './useFamilyData';
import { useFriendsData } from './useFriendsData';
import { uiText } from '../../shared/utils/uiText';

/**
 * GroupsHubScreen
 *
 * Central screen for all group-related features:
 * - Create/Join Groups
 * - View/Accept Invitations
 * - Manage Members & Roles
 * - View Group Leaderboard
 * - Leave Groups
 */
type Invitation = {
  id: string;
  families?: { name?: string };
  invited_by?: { name?: string };
  user_profiles?: { name?: string };
  invite_token: string;
};

type GroupsHubNavigation = StackNavigationProp<{
  MultiplayerLobby: {
    groupId: string;
    groupName: string;
    groupType: 'family' | 'friends';
    maxPlayers?: number;
  };
}>;

const shellColors = {
  canvas: '#120A2B',
  panel: '#080B24',
  panelSoft: 'rgba(14, 18, 48, 0.88)',
  gold: '#F8C84E',
  goldSoft: 'rgba(248, 200, 78, 0.18)',
  purple: '#6D35A8',
  purpleSoft: 'rgba(109, 53, 168, 0.35)',
  ivory: '#FFF7DA',
  muted: '#D9D2C2',
  green: '#153F27',
  line: 'rgba(248, 200, 78, 0.38)',
};

const TogetherHero = ({ language }: { language: string }) => (
  <View style={styles.shellHero}>
    <View style={styles.heroGlow} />
    <View style={styles.heroTitleRow}>
      <View style={styles.heroIcon}>
        <Ionicons name="people" size={22} color={shellColors.gold} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.heroEyebrow}>{t('familiesFriends.headerTitle', language)}</Text>
        <Text style={styles.heroTitle}>{t('friends_family_play', language)}</Text>
      </View>
    </View>
    <Text style={styles.heroSubtitle}>{t('familiesFriends.headerSubtitle', language)}</Text>
    <View style={styles.heroPillRow}>
      <View style={styles.heroPill}>
        <Ionicons name="home" size={14} color={shellColors.gold} />
        <Text style={styles.heroPillText}>{t('familiesFriends.groupTypeFamily', language)}</Text>
      </View>
      <View style={styles.heroPill}>
        <Ionicons name="person-add" size={14} color={shellColors.gold} />
        <Text style={styles.heroPillText}>{t('familiesFriends.groupTypeFriends', language)}</Text>
      </View>
    </View>
  </View>
);

const GroupsHubScreenContent = () => {
  const { language } = useLanguageStore();
  const { user } = useAuth();
  const { isGuest, endGuestSession } = useGuestUser();

  // No active tab needed - unified Groups experience
  const activeTab = 'family';
  const navigation = useNavigation<GroupsHubNavigation>();
  const [createModalVisible, setCreateModalVisible] = React.useState(false);

  // Pre-warm feature cache to prevent premium modal flashing for subscribed users
  React.useEffect(() => {
    if (user?.id) {
      // Pre-warm cache for group-related features
      preWarmFeatureCache(user.id, [
        PremiumFeature.TEAM_MANAGEMENT,
        PremiumFeature.FAMILY_ANALYTICS,
        PremiumFeature.GROUP_ANALYTICS,
        PremiumFeature.GROUP_MEMBER_MANAGEMENT
      ]);
    }
  }, [user?.id]);

  // --- Tab Buttons ---
  // Place this at the top of your render/return block:

  // --- Conditional Rendering ---
  // In your return block, wrap all existing Family content in:
  // {activeTab === 'family' && ( ... )}
  // and add this for friends:
  // {activeTab === 'friends' && (
  //   <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', marginTop: 40 }}>
  //     <Text style={{ color: '#2C7A7B', fontSize: 18 }}>Friends Hub coming soon!</Text>
  //   </View>
  // )}

  const [_inviteModalVisible, _setInviteModalVisible] = React.useState(false);
  const [_inviteFamilyId, _setInviteFamilyId] = React.useState<string | null>(null);
  const [joinByCodeVisible, setJoinByCodeVisible] = React.useState(false);
  const [shareLoading, setShareLoading] = React.useState(false);

  // Store screenshot capture: present a signed-in hub with demo family/friends
  // data instead of the sign-in gate (there is no real session in screenshot mode).
  const screenshotMode = isScreenshotMode() && !isScreenshotGuestMode();

  const { id: profileUserId, loading: fetchedProfileLoading } = useUserProfile();
  const userId = screenshotMode ? DEMO_USER_ID : profileUserId;
  const profileLoading = screenshotMode ? false : fetchedProfileLoading;
  const MAX_FAMILIES = 3; // Maximum families per user as per product requirement
  const {
    families: fetchedFamilies,
    membersByFamily: fetchedMembersByFamily,
    invitations,
    loading: fetchedFamilyLoading,
    error: fetchedFamilyError,
    refetch
  } = useFamilyData(screenshotMode ? null : userId);
  const families: any[] = screenshotMode ? [{ ...DEMO_FAMILY, name: uiText('demoFamilyName') }] : fetchedFamilies;
  const membersByFamily: { [familyId: string]: any[] } = screenshotMode
    ? { [DEMO_FAMILY.id]: DEMO_FAMILY_MEMBERS }
    : fetchedMembersByFamily;
  const loading = screenshotMode ? false : fetchedFamilyLoading;
  const error = screenshotMode ? null : fetchedFamilyError;

  // Load Friends groups data for unified Groups experience
  const {
    groups: fetchedFriendsGroups,
    membersByGroup: fetchedMembersByFriendsGroup,
    invitations: friendsInvitations,
    loading: fetchedFriendsLoading,
    error: _friendsError,
    refetch: refetchFriends
  } = useFriendsData(screenshotMode ? null : userId);
  const friendsGroups: any[] = screenshotMode ? [{ ...DEMO_FRIENDS_GROUP, name: uiText('demoFriendsGroupName') }] : fetchedFriendsGroups;
  const membersByFriendsGroup: { [groupId: string]: any[] } = screenshotMode
    ? { [DEMO_FRIENDS_GROUP.id]: DEMO_FRIENDS_MEMBERS }
    : fetchedMembersByFriendsGroup;
  const friendsLoading = screenshotMode ? false : fetchedFriendsLoading;

  // Modal state for viewing members of a specific family
  const [membersModalFamilyId, setMembersModalFamilyId] = React.useState<string | null>(null);
  const handleShowMembers = (family: { id: string }) => setMembersModalFamilyId(family.id);
  const handleCloseMembersModal = () => setMembersModalFamilyId(null);

  // Unified loading state and refetch function for both Family and Friends
  const isUnifiedLoading = loading || friendsLoading;
  const unifiedRefetch = () => {
    refetch();
    refetchFriends();
  };

  // Combine all invitations from both Family and Friends
  const _allInvitations = [...invitations, ...friendsInvitations];

  if ((isGuest && !screenshotMode) || !userId) {
    return (
      <View style={styles.shellBackground}>
        <TogetherHero language={language} />
        <View style={styles.centered}>
          <Ionicons name="people-circle-outline" size={72} color={shellColors.gold} style={{ marginBottom: 16 }} />
          <Text style={[styles.errorText, { color: shellColors.ivory }]}>{t('guestMode.multiplayerRequiredTitle', language)}</Text>
          <Text style={[styles.loadingText, { textAlign: 'center', marginBottom: 20, color: shellColors.muted }]}>
            {t('guestMode.multiplayerRequiredMessage', language) || t('familiesFriends.signInToJoinFamily', language)}
          </Text>
          <PrimaryButton title={t('guestMode.signUpButton', language)} onPress={endGuestSession} />
        </View>
      </View>
    );
  }

  // Accept/Reject Invitation
  const handleInvitationAction = async (invite: Invitation, accept: boolean) => {
    try {
      const { error } = await supabase.rpc('respond_to_family_invite', {
        invite_token: invite.invite_token,
        accept
      });
      if (error) throw error;
      Alert.alert(uiText('successTitle'), accept ? t('familiesFriends.alertSuccessInvitationAccepted', language) : t('familiesFriends.alertSuccessInvitationRejected', language));
      refetch();
    } catch (e) {
      let message = t('familiesFriends.alertErrorInvitationResponse', language);
      if (e instanceof Error) message = e.message;
      else if (typeof e === 'string') message = e;
      Alert.alert(uiText('errorTitle'), message);
    }
  };

  // Share Invite Link
  const handleShareInvite = async (invite: Invitation) => {
    setShareLoading(true);
    try {
      const url = `https://kingdombiblequiz.com/invite/${invite.invite_token}`;
      await Share.share({
        message: t('familiesFriends.shareInviteMessage', language).replace('{{url}}', url)
      });
    } catch (e) {
      let message = t('familiesFriends.alertErrorShareInvite', language);
      if (e instanceof Error) message = e.message;
      else if (typeof e === 'string') message = e;
      Alert.alert(uiText('errorTitle'), message);
    }
    setShareLoading(false);
  };

  if (profileLoading || isUnifiedLoading) {
    return (
      <View style={styles.shellBackground}>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={shellColors.gold} />
          <Text style={[styles.loadingText, { color: shellColors.gold }]}>{t('familiesFriends.loadingFamilyAndFriendsHub', language)}</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.shellBackground}>
        <View style={styles.centered}>
          <Text style={[styles.errorText, { color: shellColors.ivory }]}>{t('familiesFriends.errorLoadingFamilyHub', language)}</Text>
          <PrimaryButton title={t('familiesFriends.buttonRetry', language)} onPress={refetch} />
        </View>
      </View>
    );
  }

  // Modals: always mounted so buttons work regardless of family count
  const modals = (
    <>
      <JoinByInviteCodeModal
        visible={joinByCodeVisible}
        onClose={() => setJoinByCodeVisible(false)}
        onJoined={() => {
          setJoinByCodeVisible(false);
          refetch();
        }}
      />
      <CreateGroupModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onCreated={() => {
          setCreateModalVisible(false);
          refetch();
        }}
      />
    </>
  );

  // No family: show create/join and invitations if any
  if (!families.length) {
    return (
      <View style={styles.shellBackground}>
        {modals}
        <TogetherHero language={language} />
        <ScrollView contentContainerStyle={styles.container}>
          {/* Live Screen Notice for Large Groups - Web only */}
          {Platform.OS === 'web' && (
            <View style={styles.liveScreenNotice}>
              <Text style={styles.liveScreenNoticeIcon}>ℹ️</Text>
              <Text style={styles.liveScreenNoticeText}>
                {t('familiesFriends.liveScreenNoticeText', language)}
              </Text>
            </View>
          )}

          {true && (
            <>
              <View style={styles.premiumContentContainer}>
                <Text style={styles.premiumEmptyStateText}>{t('familiesFriends.emptyStateNotInFamily', language)}</Text>
                <PrimaryButton title={t('familiesFriends.buttonCreateFamily', language)} onPress={() => setCreateModalVisible(true)} />
                <PrimaryButton title={t('familiesFriends.buttonJoinByInviteCode', language)} onPress={() => setJoinByCodeVisible(true)} style={styles.buttonSpacing} />
                {invitations.length > 0 && (
                  <PrimaryButton title={t('familiesFriends.buttonViewInvitations', language).replace('{{count}}', invitations.length.toString())} onPress={() => {}} style={styles.buttonSpacing} />
                )}
              </View>
              {invitations.length > 0 && (
                <Card variant="frosted" style={{ marginBottom: 18, backgroundColor: shellColors.panelSoft, borderColor: shellColors.line }}>
                  <Text style={[styles.sectionTitle, { color: shellColors.ivory }]}>{t('familiesFriends.sectionTitlePendingInvitations', language)}</Text>
                  {invitations.map((invite: Invitation) => (
                    <InvitationCard
                      key={invite.id}
                      invite={invite}
                      onAccept={() => handleInvitationAction(invite, true)}
                      onReject={() => handleInvitationAction(invite, false)}
                      onShare={() => handleShareInvite(invite)}
                      shareLoading={shareLoading}
                      styles={styles}
                    />
                  ))}
                </Card>
              )}
            </>
          )}
        </ScrollView>
      </View>
    );
  }

  return (
    <>
      <View style={styles.shellBackground}>
      <JoinByInviteCodeModal
        visible={joinByCodeVisible}
        onClose={() => setJoinByCodeVisible(false)}
        onJoined={() => {
          setJoinByCodeVisible(false);
          refetch();
        }}
      />
      <CreateGroupModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onCreated={() => {
          setCreateModalVisible(false);
          refetch();
        }}
      />
      <View style={{ flex: 1 }}>
        <TogetherHero language={language} />

        {activeTab !== 'friends' ? (
          <ScrollView contentContainerStyle={styles.container}>
            {/* Show Create Family and Join by Invite Code buttons if user is in fewer than MAX_FAMILIES */}
            {families.length < MAX_FAMILIES && (
              <>
                <PremiumFeatureGate
                  feature={PremiumFeature.TEAM_MANAGEMENT}
                  showUpgradePrompt={true}
                  customMessage={t('familiesFriends.premiumGroupGateMessage', language)}
                >
                  <PrimaryButton title={t('familiesFriends.buttonCreateFamilyOrFriendsGroup', language)} onPress={() => setCreateModalVisible(true)} style={styles.buttonSpacing} />
                </PremiumFeatureGate>
                <PrimaryButton title={t('familiesFriends.buttonJoinByInviteCode', language)} onPress={() => setJoinByCodeVisible(true)} style={styles.buttonSpacing} />
              </>
            )}
            {/* Render each family the user belongs to */}
            {families.map((family) => {
              const members = membersByFamily[family.id] || [];
              const userRole = members.find((m) => m.user_id === userId)?.role || 'Member';
              return (
                <FamilyCard
                  key={`family-${family.id}`}
                  family={family}
                  members={members}
                  userRole={userRole}
                  onShareLink={() => handleShareInvite({ ...family, invite_token: family.family_id })}
                  onShareCode={() => {
                    try {
                      if (Platform.OS === 'web') {
                        navigator.clipboard.writeText(family.family_id);
                      } else {
                        Clipboard.setStringAsync(family.family_id);
                      }
                      Alert.alert(t('familiesFriends.alertSuccessCodeCopied', language).split('.')[0], t('familiesFriends.alertSuccessCodeCopied', language));
                    } catch {
                      Alert.alert(uiText('errorTitle'), t('familiesFriends.alertErrorCopyCode', language));
                    }
                  }}
                  onLeaveFamily={async () => {
                    try {
                      const { error } = await supabase.rpc('leave_family', { _family_id: family.id });
                      if (error) throw error;
                      Alert.alert(uiText('successTitle'), t('familiesFriends.alertSuccessLeftFamily', language));
                      unifiedRefetch();
                    } catch (e) {
                      let message = t('familiesFriends.alertErrorLeaveFamily', language);
                      if (e instanceof Error) message = e.message;
                      else if (typeof e === 'string') message = e;
                      Alert.alert(uiText('errorTitle'), message);
                    }
                  }}
                  onShowMembers={() => handleShowMembers(family)}
                  onGoToLobby={(family) => navigation.navigate('MultiplayerLobby', {
                    groupId: family.id as string,
                    groupName: family.name as string,
                    groupType: 'family',
                    maxPlayers: 7
                  })}
                  styles={styles}
                />
              );
            })}

            {/* Render each friends group the user belongs to */}
            {friendsGroups.map((friendsGroup) => {
              const members = membersByFriendsGroup[friendsGroup.id] || [];
              const userRole = members.find((m) => m.user_id === userId)?.role || 'Member';
              return (
                <FriendsCard
                  key={`friends-${friendsGroup.id}`}
                  group={friendsGroup}
                  members={members}
                  userRole={userRole}
                  onShareLink={() => handleShareInvite({ ...friendsGroup, invite_token: friendsGroup.group_code })}
                  onShareCode={() => {
                    try {
                      if (Platform.OS === 'web') {
                        navigator.clipboard.writeText(friendsGroup.group_code);
                      } else {
                        Clipboard.setStringAsync(friendsGroup.group_code);
                      }
                      Alert.alert(t('familiesFriends.alertSuccessFriendsCodeCopied', language).split('.')[0], t('familiesFriends.alertSuccessFriendsCodeCopied', language));
                    } catch {
                      Alert.alert(uiText('errorTitle'), t('familiesFriends.alertErrorCopyCode', language));
                    }
                  }}
                  onLeaveGroup={async () => {
                    try {
                      const { error } = await supabase.rpc('leave_friends_group', { _group_id: friendsGroup.id });
                      if (error) throw error;
                      Alert.alert(uiText('successTitle'), t('familiesFriends.alertSuccessLeftFriendsGroup', language));
                      unifiedRefetch();
                    } catch (e) {
                      let message = t('familiesFriends.alertErrorLeaveFriendsGroup', language);
                      if (e instanceof Error) message = e.message;
                      else if (typeof e === 'string') message = e;
                      Alert.alert(uiText('errorTitle'), message);
                    }
                  }}
                  onShowMembers={() => handleShowMembers(friendsGroup)}
                  onGoToLobby={(group) => navigation.navigate('MultiplayerLobby', {
                    groupId: group.id as string,
                    groupName: group.name as string,
                    groupType: 'friends'
                  })}
                  styles={styles}
                />
              );
            })}
            {/* Family Leaderboard */}
            {families.length > 0 && (
              <View style={{ marginBottom: 18 }}>
                <FamilyLeaderboard
                  familyIds={families.map(f => f.id)}
                  currentUserId={userId || ''}
                />
              </View>
            )}
            {families.length === 0 && (
              <View style={[styles.premiumCard, { backgroundColor: shellColors.panelSoft, borderColor: shellColors.line }]}>
                <Text style={[styles.premiumCardTitle, { color: shellColors.ivory }]}>{t('familiesFriends.premiumCardFamilyLeaderboard', language)}</Text>
                <Text style={[styles.premiumCardSubtitle, { color: shellColors.muted }]}>{uiText('leaderboardJoinPrompt')}</Text>
              </View>
            )}
            {/* Invitations management, if any */}
            {invitations.length > 0 && (
              <Card variant="frosted" style={{ marginBottom: 18, backgroundColor: shellColors.panelSoft, borderColor: shellColors.line }}>
                <Text style={[styles.sectionTitle, { color: shellColors.ivory }]}>{uiText('pendingInvitations')}</Text>
                {invitations.map(invite => (
                  <InvitationCard
                    key={invite.id}
                    invite={invite}
                    onAccept={() => handleInvitationAction(invite, true)}
                    onReject={() => handleInvitationAction(invite, false)}
                    onShare={() => handleShareInvite(invite)}
                    shareLoading={shareLoading}
                    styles={styles}
                  />
                ))}
              </Card>
            )}
          </ScrollView>
        ) : (
          <ScrollView contentContainerStyle={styles.container}>
            {/* Show Create Family or Friends Group and Join by Invite Code buttons if user is in fewer than MAX_FAMILIES */}
            {families.length < MAX_FAMILIES && (
              <>
                <PremiumFeatureGate
                  feature={PremiumFeature.TEAM_MANAGEMENT}
                  showUpgradePrompt={true}
                  customMessage={t('familiesFriends.premiumGroupGateMessage', language)}
                >
                  <PrimaryButton title={t('familiesFriends.buttonCreateFamilyOrFriendsGroup', language)} onPress={() => setCreateModalVisible(true)} style={styles.buttonSpacing} />
                </PremiumFeatureGate>
                <PrimaryButton title={t('familiesFriends.buttonJoinByInviteCode', language)} onPress={() => setJoinByCodeVisible(true)} style={styles.buttonSpacing} />
              </>
            )}
            {/* Render each friend group the user belongs to (placeholder: using families for now) */}
            {families.map((family) => {
              const members = membersByFamily[family.id] || [];
              const userRole = members.find((m) => m.user_id === userId)?.role || 'Member';
              return (
                <FriendsCard
                  key={family.id}
                  group={family}
                  members={members}
                  userRole={userRole}
                  onShareLink={() => handleShareInvite({ ...family, invite_token: family.family_id })}
                  onShareCode={() => {
                    try {
                      if (Platform.OS === 'web') {
                        navigator.clipboard.writeText(family.family_id);
                      } else {
                        Clipboard.setStringAsync(family.family_id);
                      }
                      Alert.alert(t('familiesFriends.alertSuccessFriendsCodeCopied', language).split('.')[0], t('familiesFriends.alertSuccessFriendsCodeCopied', language));
                    } catch {
                      Alert.alert(uiText('errorTitle'), t('familiesFriends.alertErrorCopyCode', language));
                    }
                  }}
                  onLeaveGroup={async () => {
                    try {
                      const { error } = await supabase.rpc('leave_family', { _family_id: family.id });
                      if (error) throw error;
                      Alert.alert(uiText('successTitle'), t('familiesFriends.alertSuccessLeftFriendsGroup', language));
                      refetch();
                    } catch (e) {
                      let message = t('familiesFriends.alertErrorLeaveFriendsGroup', language);
                      if (e instanceof Error) message = e.message;
                      else if (typeof e === 'string') message = e;
                      Alert.alert(uiText('errorTitle'), message);
                    }
                  }}
                  onShowMembers={() => handleShowMembers(family)}
                  onGoToLobby={(group) => navigation.navigate('MultiplayerLobby', { groupId: group.id as string, groupName: group.name as string, groupType: 'family' })}
                  styles={styles}
                />
              );
            })}
            {/* Premium Friends Leaderboard */}
            <View style={[styles.premiumCard, { backgroundColor: shellColors.panelSoft, borderColor: shellColors.line }]}>
              <Text style={[styles.premiumCardTitle, { color: shellColors.ivory }]}>{t('familiesFriends.premiumCardFriendsLeaderboard', language)}</Text>
              <Text style={[styles.premiumCardSubtitle, { color: shellColors.muted }]}>{t('familiesFriends.premiumCardFriendsLeaderboardSubtitle', language)}</Text>
              <View style={[styles.premiumPlaceholder, { backgroundColor: 'rgba(255,247,218,0.08)' }]}>
                <Text style={[styles.premiumPlaceholderText, { color: shellColors.muted }]}>{t('familiesFriends.premiumCardComingSoon', language)}</Text>
              </View>
            </View>
            {/* Invitations management, if any */}
            {invitations.length > 0 && (
              <Card variant="frosted" style={{ marginBottom: 18, backgroundColor: shellColors.panelSoft, borderColor: shellColors.line }}>
                <Text style={[styles.sectionTitle, { color: shellColors.ivory }]}>{uiText('pendingInvitations')}</Text>
                {invitations.map(invite => (
                  <InvitationCard
                    key={invite.id}
                    invite={invite}
                    onAccept={() => handleInvitationAction(invite, true)}
                    onReject={() => handleInvitationAction(invite, false)}
                    onShare={() => handleShareInvite(invite)}
                    shareLoading={shareLoading}
                    styles={styles}
                  />
                ))}
              </Card>
            )}
          </ScrollView>
        )}
      </View>
      {/* Render FamilyMembersModal for the selected family */}
      {membersModalFamilyId && (
        <FamilyMembersModal
          visible={!!membersModalFamilyId}
          onClose={handleCloseMembersModal}
          members={membersByFamily[membersModalFamilyId] || []}
          currentUserId={userId}
          isAdmin={['owner', 'admin'].includes(
            (membersByFamily[membersModalFamilyId] || []).find((m) => m.user_id === userId)?.role || ''
          )}
          onMakeAdmin={async (userIdToPromote: string) => {
            if (!membersModalFamilyId) return;
            try {
              const { error } = await supabase.rpc('make_family_member_admin', {
                p_family_id: membersModalFamilyId,
                p_user_id: userIdToPromote
              });
              if (error) throw error;
              Alert.alert(uiText('successTitle'), t('familiesFriends.alertSuccessMemberPromoted', language));
              handleCloseMembersModal();
              refetch();
            } catch (e: unknown) {
              Alert.alert(uiText('errorTitle'), e instanceof Error ? e.message : t('familiesFriends.alertErrorPromoteMember', language));
            }
          }}
        />
      )}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  lightBackground: {
  },
  shellBackground: {
    flex: 1,
    backgroundColor: shellColors.canvas,
  },
  shellHero: {
    marginHorizontal: 20,
    marginTop: 18,
    marginBottom: 4,
    borderRadius: 24,
    padding: 22,
    overflow: 'hidden',
    backgroundColor: shellColors.panel,
    borderWidth: 1,
    borderColor: shellColors.line,
    ...Platform.select({
      ios: {
        shadowColor: shellColors.gold,
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.22,
        shadowRadius: 22,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0px 14px 28px rgba(248, 200, 78, 0.16)',
      },
    }),
  },
  heroGlow: {
    position: 'absolute',
    right: -68,
    top: -72,
    width: 180,
    height: 180,
    borderRadius: 90,
    backgroundColor: shellColors.purpleSoft,
  },
  heroTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  heroIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: shellColors.goldSoft,
    borderWidth: 1,
    borderColor: shellColors.line,
  },
  heroEyebrow: {
    color: shellColors.gold,
    fontSize: 13,
    fontWeight: '900',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  heroTitle: {
    color: shellColors.ivory,
    fontSize: 30,
    lineHeight: 36,
    fontWeight: '900',
    marginTop: 4,
  },
  heroSubtitle: {
    color: shellColors.muted,
    fontSize: 15,
    lineHeight: 22,
    marginTop: 14,
  },
  heroPillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 16,
  },
  heroPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: 'rgba(255,247,218,0.08)',
    borderWidth: 1,
    borderColor: shellColors.line,
  },
  heroPillText: {
    color: shellColors.ivory,
    fontSize: 12,
    fontWeight: '800',
  },
  premiumInvitationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  premiumContentContainer: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  premiumEmptyStateText: {
    color: shellColors.ivory,
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24
  },
  container: {
    flexGrow: 1,
    padding: 20,
    paddingTop: 16,
    alignItems: 'stretch'
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
    backgroundColor: '#e0e7ef',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontSize: 24
  },
  avatarImg: {
    width: 32,
    height: 32,
    borderRadius: 16,
    resizeMode: 'cover'
  },
  memberRole: {
    marginLeft: 8,
    color: '#4F46E5',
    fontWeight: 'bold',
    fontSize: 14
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1D1A23',
    marginBottom: 12
  },
  infoText: {
    color: '#464555',
    fontSize: 15,
    marginBottom: 6
  },
  buttonSpacing: {
    marginTop: 10
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10
  },
  memberName: {
    fontSize: 16,
    color: '#1D1A23',
    fontWeight: '600',
    flex: 1
  },
  inviteFamily: {
    fontWeight: 'bold',
    color: '#1D1A23',
    marginRight: 8
  },
  inviteFrom: {
    color: '#464555',
    fontSize: 14
  },
  subtitle: {
    fontSize: 18,
    color: '#464555',
    marginBottom: 10,
    textAlign: 'center'
  },
  loadingText: {
    marginTop: 16,
    color: shellColors.gold,
    fontSize: 18,
    fontWeight: '600'
  },
  errorTitle: {
    color: '#d32f2f',
    fontWeight: 'bold',
    fontSize: 20,
    marginBottom: 8
  },
  errorText: {
    color: shellColors.ivory,
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 16
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1D1A23',
    textAlign: 'center',
    marginBottom: 16
  },
  tabRow: {
    flexDirection: 'row',
    marginBottom: 16
  },
  tab: {
    flex: 1,
    padding: 12,
    backgroundColor: '#E5E7EB',
    alignItems: 'center',
    borderRadius: 8,
    marginHorizontal: 4
  },
  tabActive: {
    backgroundColor: '#4F46E5'
  },
  tabText: {
    fontWeight: 'bold',
    color: '#374151'
  },
  premiumTabGradient: {
    alignItems: 'center',
    flex: 1,
    minHeight: 48,
    marginHorizontal: 4
  },

  tabTextActive: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
    opacity: 1
  },
  // Premium Design System Styles
  premiumHeaderContainer: {
    alignItems: 'center',
    marginBottom: 32,
    paddingHorizontal: 20,
    paddingTop: 16,
  },
  premiumHeaderBadge: {
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    borderRadius: 25,
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(79, 70, 229, 0.2)',
    ...Platform.select({
      ios: {
        shadowColor: '#1D1A23',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0px 4px 12px rgba(29, 26, 35, 0.08)',
      },
    }),
  },
  premiumHeaderText: {
    color: '#1D1A23',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 1,
  },
  premiumSubtitle: {
    color: '#464555',
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
    lineHeight: 22,
    paddingHorizontal: 16,
    marginBottom: 20,
  },
  premiumTabContainer: {
    flexDirection: 'row',
    marginBottom: 32,
    paddingHorizontal: 20,
    paddingVertical: 8,
    gap: 12,
    marginTop: 4,
  },
  premiumTabButton: {
    flex: 1,
    borderRadius: 16,
    overflow: 'hidden',
    transform: [{ scale: 0.98 }],
    ...Platform.select({
      ios: {
        shadowColor: '#1D1A23',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0px 2px 8px rgba(29, 26, 35, 0.06)',
      },
    }),
  },
  premiumTabActive: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'center',
    backgroundColor: '#4F46E5',
    borderWidth: 1.5,
    borderColor: '#4F46E5',
    borderRadius: 16,
  },
  premiumTabInactive: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 16,
  },
  premiumTabTextActive: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  premiumTabTextInactive: {
    color: '#777587',
    fontSize: 16,
    fontWeight: '600',
  },
  premiumTabContentRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  premiumTabIconContainer: {
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    borderRadius: 8,
    padding: 4,
    marginRight: 8,
  },
  premiumTabIconInactive: {
    backgroundColor: 'rgba(119, 117, 135, 0.08)',
  },
  premiumTabIcon: {
    fontSize: 18,
    color: '#4F46E5',
  },
  premiumTabIconTextInactive: {
    fontSize: 18,
    color: '#777587',
  },
  // Premium Card Styles
  premiumCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 20,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    ...Platform.select({
      ios: {
        shadowColor: '#1D1A23',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0px 4px 12px rgba(29, 26, 35, 0.08)',
      },
    }),
  },
  premiumCardTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1D1A23',
    marginBottom: 8,
  },
  premiumCardSubtitle: {
    fontSize: 15,
    fontWeight: '500',
    color: '#777587',
    marginBottom: 16,
  },
  premiumPlaceholder: {
    backgroundColor: '#F3F2F8',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 60,
  },
  premiumPlaceholderText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#777587',
    textAlign: 'center',
  },
  // Live Screen Notice Styles
  liveScreenNotice: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 12,
    marginHorizontal: 16,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderLeftWidth: 3,
    borderLeftColor: '#4F46E5',
    ...Platform.select({
      ios: {
        shadowColor: '#1D1A23',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0px 2px 4px rgba(29, 26, 35, 0.06)',
      },
    }),
  },
  liveScreenNoticeIcon: {
    fontSize: 16,
    marginRight: 8,
    marginTop: 1,
  },
  liveScreenNoticeText: {
    flex: 1,
    fontSize: 14,
    color: '#464555',
    lineHeight: 20,
  },
  liveScreenNoticeLink: {
    fontWeight: '600',
    color: '#4F46E5',
  },

});

const GroupsHubScreen: React.FC = () => (
  <PaperProvider>
    <GroupsHubScreenContent />
  </PaperProvider>
);

export default GroupsHubScreen;
